import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { apiError } from "../lib/api";
import { LOGO_URL } from "../lib/data";
import { toast, Toaster } from "sonner";

export default function AdminLogin() {
  const { user, login, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (!ready) return <div className="min-h-screen bg-cream" />;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Invalid email or password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 relative">
      <Toaster position="bottom-right" />
      <div className="lilac-blur" style={{ top: "-200px", right: "-200px", opacity: 0.5 }} />
      <div className="brown-blur" style={{ bottom: "-200px", left: "-200px", opacity: 0.4 }} />

      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-cream-alt border border-[#E6DEC3] rounded-[2rem] p-9 relative"
      >
        <div className="flex items-center gap-3 mb-7">
          <img src={LOGO_URL} alt="Barks & Co" className="h-12 w-12 rounded-full" />
          <div>
            <div className="font-serif text-2xl text-brown-dark leading-tight">
              Studio Admin
            </div>
            <div className="eyebrow" style={{ fontSize: "0.6rem" }}>
              Barks &amp; Co
            </div>
          </div>
        </div>

        <h1 className="font-serif text-3xl text-brown-dark mb-2">Welcome back.</h1>
        <p className="text-text-secondary text-sm mb-6">
          Sign in to manage bookings, services and availability.
        </p>

        <label className="block eyebrow mb-2">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-login-email"
          required
          className="w-full bg-cream border border-[#E6DEC3] rounded-full px-5 py-3.5 text-brown-dark mb-4 focus:outline-none focus:ring-1 focus:ring-lilac focus:border-lilac"
        />

        <label className="block eyebrow mb-2">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-login-password"
          required
          className="w-full bg-cream border border-[#E6DEC3] rounded-full px-5 py-3.5 text-brown-dark mb-7 focus:outline-none focus:ring-1 focus:ring-lilac focus:border-lilac"
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="btn-primary w-full disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={16} /> Signing in…
            </>
          ) : (
            <>
              <Lock size={14} /> Sign In
            </>
          )}
        </button>
      </form>
    </div>
  );
}
